import { useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Badge } from '../../components/ui/badge';
import { TrendingDown, Clock, AlertTriangle, DollarSign } from 'lucide-react';

const PAYER_DATA = {
  'Blue Cross': { avgDenialRate: '4.2%', avgDaysToPay: 18, topDenials: ['Missing modifier', 'Timely filing', 'Bundling'], reimbursementRate: '72%', tip: 'BCBS is strict on modifier 25 with same-day procedures. Always attach supporting documentation.', color: '#3b82f6' },
  'Aetna': { avgDenialRate: '6.8%', avgDaysToPay: 22, topDenials: ['Prior auth missing', 'Medical necessity', 'Duplicate claim'], reimbursementRate: '68%', tip: 'Aetna requires pre-authorization for many diagnostic tests after 3/year. Check patient YTD utilization.', color: '#6b21a8' },
  'UnitedHealth': { avgDenialRate: '3.5%', avgDaysToPay: 16, topDenials: ['Coordination of benefits', 'Timely filing', 'Invalid NPI'], reimbursementRate: '74%', tip: 'UHC has the fastest turnaround. Double-check NPI numbers — they auto-reject invalid NPIs.', color: '#0369a1' },
  'Cigna': { avgDenialRate: '5.1%', avgDaysToPay: 20, topDenials: ['Pre-auth required', 'Out of network', 'Medical necessity'], reimbursementRate: '70%', tip: 'Cigna frequently requires pre-auth for specialist procedures. Always verify before submission.', color: '#ea580c' },
  'Medicare': { avgDenialRate: '8.9%', avgDaysToPay: 28, topDenials: ['Medical necessity (LCD/NCD)', 'Wrong code for age', 'ABN not on file'], reimbursementRate: '62%', tip: 'Medicare is the strictest payer. Always use age-appropriate codes (AWV instead of preventive E/M for 65+).', color: '#059669' },
};

const PayerIntelligenceCard = ({ payerName, children }) => {
  const [open, setOpen] = useState(false);
  const data = PAYER_DATA[payerName] || { avgDenialRate: 'N/A', avgDaysToPay: 'N/A', topDenials: [], reimbursementRate: 'N/A', tip: 'No intelligence data available for this payer.', color: '#888' };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <span
          onMouseEnter={() => setOpen(true)}
          onMouseLeave={() => setOpen(false)}
          style={{ cursor: 'pointer', borderBottom: '1px dotted #ccc' }}
          data-testid={`payer-intel-trigger-${payerName.replace(/\s+/g, '-').toLowerCase()}`}
        >
          {children || payerName}
        </span>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start" side="bottom" data-testid={`payer-intel-card-${payerName.replace(/\s+/g, '-').toLowerCase()}`}>
        <div style={{ borderTop: `3px solid ${data.color}` }}>
          <div style={{ padding: '10px 12px', borderBottom: '0.5px solid #f0f0f0' }}>
            <div className="flex items-center justify-between">
              <span style={{ fontSize: 13, fontWeight: 600, color: '#111' }}>{payerName}</span>
              <Badge style={{ fontSize: 8, border: 'none', background: '#f0f0f0', color: '#888' }}>Payer Intel</Badge>
            </div>
          </div>
          <div style={{ padding: '10px 12px' }} className="space-y-2.5">
            <div className="grid grid-cols-2 gap-2">
              <div className="flex items-center gap-1.5" style={{ padding: '6px 8px', borderRadius: 6, background: '#fef2f2' }}>
                <TrendingDown style={{ width: 11, height: 11, color: '#ef4444' }} />
                <div><p style={{ fontSize: 8, color: '#aaa', textTransform: 'uppercase' }}>Denial Rate</p><p style={{ fontSize: 13, fontWeight: 600, color: '#991b1b' }}>{data.avgDenialRate}</p></div>
              </div>
              <div className="flex items-center gap-1.5" style={{ padding: '6px 8px', borderRadius: 6, background: '#f0f4ff' }}>
                <Clock style={{ width: 11, height: 11, color: '#3b82f6' }} />
                <div><p style={{ fontSize: 8, color: '#aaa', textTransform: 'uppercase' }}>Days to Pay</p><p style={{ fontSize: 13, fontWeight: 600, color: '#1e40af' }}>{data.avgDaysToPay}</p></div>
              </div>
            </div>
            <div style={{ padding: '6px 8px', borderRadius: 6, background: '#f0fdf4' }} className="flex items-center gap-1.5">
              <DollarSign style={{ width: 11, height: 11, color: '#22c55e' }} />
              <div><p style={{ fontSize: 8, color: '#aaa', textTransform: 'uppercase' }}>Avg Reimbursement</p><p style={{ fontSize: 12, fontWeight: 600, color: '#166534' }}>{data.reimbursementRate}</p></div>
            </div>
            <div>
              <p style={{ fontSize: 9, color: '#aaa', textTransform: 'uppercase', marginBottom: 4 }}>Top Denial Reasons</p>
              <div className="flex flex-wrap gap-1">
                {data.topDenials.map((r, i) => (
                  <span key={i} className="flex items-center gap-0.5" style={{ fontSize: 9, padding: '2px 6px', borderRadius: 10, background: '#fffbeb', color: '#92400e', border: '0.5px solid #fde68a' }}>
                    <AlertTriangle style={{ width: 8, height: 8 }} /> {r}
                  </span>
                ))}
              </div>
            </div>
            <div style={{ padding: '6px 8px', borderRadius: 6, background: '#fafafa', borderLeft: `2px solid ${data.color}` }}>
              <p style={{ fontSize: 10, color: '#555', lineHeight: '1.5' }}>{data.tip}</p>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default PayerIntelligenceCard;
